// @ts-nocheck
import dayjs from "dayjs";
import _ from "lodash";
import { getUniversalDateTimeFormat } from "../calcTime";

/**
 * Groups reimbursements by contractorId (or positionId if contractorId is missing) and returns enriched summary rows.
 *
 * @param {Object} params
 * @param {Array} params.reimbursements - Array of reimbursement objects
 * @param {Array} params.contractorArr - Array of contractor details
 * @returns {Array} - Grouped and aggregated reimbursement rows
 */
export function groupReimbursementsByContractor({
  reimbursements,
  contractorArr,
}) {
  if (!Array.isArray(reimbursements)) return [];
  const contractorMap = _.keyBy(contractorArr ?? [], "_id");
  // Filter out reimbursements with no contractorId or positionId
  const filtered = reimbursements.filter(
    (r) => !!r?.contractorId || !!r?.positionId,
  );
  // Group by contractorId, fallback to positionId
  const grouped = _.groupBy(
    filtered,
    (r) => r?.contractorId || `pos_${r?.positionId}`,
  );
  const rows = Object.entries(grouped).map(([groupKey, items]) => {
    let claimedAmount = 0;
    let approvedAmount = 0;
    let pendings = 0;
    let rStatus = "N/A";
    const rTypesSet = new Set();
    const positionsSet = new Set();
    items?.forEach((r) => {
      const status = r?.reImbursementStatus?.toLowerCase();
      if (
        ["pending", "accepted"].includes(status) &&
        typeof r?.claimAmount === "number"
      ) {
        claimedAmount += r?.claimAmount ?? 0;
      }
      if (status === "accepted" && typeof r?.approvedAmount === "number") {
        approvedAmount += r?.approvedAmount ?? 0;
      }
      if (status === "pending") {
        pendings += 1;
        rStatus = "Pending";
      }
      if (r?.reImbursementType) {
        rTypesSet.add(r.reImbursementType);
      }
      if (r?.positionName || r?.positionStartDate) {
        const display = `${r?.positionName ?? "N/A"}${r?.positionOrder ? `-${r?.positionOrder}` : ""}`;
        if (r?.positionName) {
          positionsSet.add(display);
        }
      }
    });
    if (rStatus === "N/A" && items?.length > 0) {
      rStatus = items?.[items.length - 1]?.reImbursementStatus ?? "N/A";
    }
    const first = items?.[0] ?? {};
    const contractorId = first?.contractorId ?? null;
    const contractorData = contractorId
      ? (contractorMap?.[contractorId] ?? {})
      : {};
    const statusInfo = _.groupBy(items ?? [], "reImbursementStatus");
    const positionsArr = Array.from(positionsSet);
    return {
      contractorId,
      contractorName: contractorData?.name ?? null,
      preferredName: contractorData?.preferredName ?? null,
      contractorEmail: contractorData?.email ?? null,
      claimedAmount,
      approvedAmount,
      pendings,
      rStatus,
      rType: Array.from(rTypesSet).join(", "),
      reimbursements: items ?? [],
      positionId: first?.positionId ?? null,
      positionName: first?.positionName ?? null,
      positionStartDate: first?.positionStartDate ?? null,
      moreNo: positionsArr.length - 3,
      positionDisplay:
        positionsArr.length > 3
          ? [...positionsArr.slice(0, 3)].join("\n")
          : positionsArr.join("\n"),
      hoverData: positionsArr.length > 3 ? positionsArr.slice(3) : [],
      sessionId: groupKey,
      statusInfo: Object.entries(statusInfo ?? {})
        ?.map(([key, value]) => `${key}: ${value?.length ?? 0}`)
        .join("\n"),
      city: contractorData?.city || null,
      state: contractorData?.state || null,
    };
  });
  return rows;
}

/**
 * it'll be of a particular contractor.
 */
export function getReimbursementStatusTotals({ reimbursements }) {
  const groupedData = _.groupBy(reimbursements, "reImbursementStatus");
  let finalData = {
    approvedAmount: 0,
    claimedAmount: 0,
    pendingAmount: 0,
  };
  finalData.pendingAmount = parseFloat(
    _.sumBy(
      groupedData?.[REIMBURSEMNET_STATUS.PENDING],
      (item) => item?.claimAmount || 0,
    ),
  ).toFixed(2);
  finalData.claimedAmount = parseFloat(
    _.sumBy(reimbursements, (item) => item?.claimAmount || 0),
  ).toFixed(2);
  finalData.approvedAmount = parseFloat(
    _.sumBy(
      groupedData?.[REIMBURSEMNET_STATUS.ACCEPTED],
      (item) => item?.approvedAmount || 0,
    ),
  ).toFixed(2);
  return finalData;
}

/**
 * Groups deduction entries by contractor (or positionId if contractorId is missing) and returns summary rows.
 *
 * @param {Object} params
 * @param {Array} params.deductions - Array of deduction objects
 * @param {Array} params.contractorArr - Array of contractor objects
 * @returns {Array} - Grouped and aggregated deduction rows
 */
export function groupDeductionsByContractor({ deductions, contractorArr }) {
  if (!Array.isArray(deductions)) return [];
  const contractorMap = _.keyBy(contractorArr ?? [], "_id");
  // Only include those with contractorId or positionId
  const filtered = deductions.filter(
    (d) => !!d?.contractorId || !!d?.positionId,
  );
  // Group by contractorId, fallback to positionId
  const grouped = _.groupBy(
    filtered,
    (d) => d?.contractorId || `pos_${d?.positionId}`,
  );
  const rows = Object.entries(grouped).map(([groupKey, items]) => {
    const first = items?.[0] ?? {};
    const contractorId = first?.contractorId ?? null;
    const contractor = contractorId ? contractorMap?.[contractorId] : null;
    const positionsSet = new Set();
    let totalAmount = 0;
    items?.forEach((item) => {
      const name = `${item?.positionName ?? "N/A"}${item?.positionOrder ? `-${item?.positionOrder}` : ""}`;
      const date = item?.positionStartDate ?? "No Start Date";
      if (item?.positionName) {
        positionsSet.add(`${name}`);
      }
      totalAmount += item?.deductionAmount ?? 0;
    });
    const positionsArr = Array.from(positionsSet);
    return {
      contractorId,
      contractorName: contractor?.name ?? "N/A",
      preferredName: contractor?.preferredName ?? null,
      contractorEmail: contractor?.email ?? null,
      moreNo: positionsArr.length - 3,
      positionDisplay:
        positionsArr.length > 3
          ? [...positionsArr.slice(0, 3)].join("\n")
          : positionsArr.join("\n"),
      hoverData: positionsArr.length > 3 ? positionsArr.slice(3) : [],
      deductionType: "Other",
      amount: totalAmount,
      deductions: items ?? [],
      positionId: first?.positionId ?? null,
      sessionId: groupKey,
      city: contractor?.city || null,
      state: contractor?.state || null,
    };
  });
  return rows;
}

export const ADJUSTMENT_TYPES = {
  REIMBURSEMENT: "reimbursement",
  DEDUCTION: "deduction",
  NON_TAXABLE_CHARGES: "non-taxable-charges",
};

export const REIMBURSEMNET_STATUS = {
  PENDING: "Pending",
  ACCEPTED: "Accepted",
  REJECTED: "Rejected",
};

/**
 * Groups reimbursement data by date and category, and returns formatted strings.
 * Format: "YYYY-MM-DD - category - $amount"
 *
 * @param {Array} data - Array of reimbursement entries
 * @returns {Array<string>} - Array of formatted summary strings
 */
export function formatReimbursementGroupedDataForPO(data) {
  return _(data)
    .groupBy((item) => {
      const date = dayjs(item.expanseDate).format("YYYY-MM-DD");
      const category =
        item.reImbursementCategoryType || item?.reImbursementType || "N/A";
      return `${date}__${category}`;
    })
    .map((items, key) => {
      const [date, category] = key.split("__");
      const amount = _.sumBy(items, "approvedAmount") || 0;
      return `${date} - ${category} - $${amount}`;
    })
    .orderBy() // optional: sort alphabetically by default
    .value();
}

/**
 * Groups deductions data by date and category, and returns formatted strings.
 * Format: "YYYY-MM-DD - category - $amount"
 *
 * @param {Array} data - Array of deductions entries
 * @returns {Array<string>} - Array of formatted summary strings
 */
export function formatDeductionsGroupedDataForPO(data) {
  return _(data)
    .groupBy((item) => {
      const category =
        item.deductionCategoryType || item?.categoryType || "N/A";
      return `${category}`;
    })
    .map((items, key) => {
      const category = key;
      const amount = _.sumBy(items, "deductionAmount") || 0;
      return `${category} - $${amount}`;
    })
    .orderBy() // optional: sort alphabetically by default
    .value();
}

/**
 * Groups non-taxable charges by category and returns grouped values
 * including separate fields for notes and dates.
 *
 * @param {Object} obj
 * @param {Array} obj.nonTaxableCharges - Array of non-taxable charges
 * @returns {Array<Object>} - Array of grouped charge summaries
 */
export function groupNonTaxableChargesByCategoryType({
  nonTaxableCharges = [],
}) {
  if (!Array.isArray(nonTaxableCharges)) return [];

  // Filter valid charges with category type
  const filtered = nonTaxableCharges.filter(
    (item) => !!item.nonTaxableCategoryType,
  );

  // Group by nonTaxableCategoryType
  const grouped = _.groupBy(filtered, "nonTaxableCategoryType");

  const rows = Object.entries(grouped).map(([categoryType, items]) => {
    let totalAmount = 0;

    const notesSet = new Set();
    const datesSet = new Set();

    items.forEach((item) => {
      totalAmount += item.nonTaxableAmount || 0;

      if (item.nonTaxableNotes) {
        notesSet.add(item.nonTaxableNotes);
      }

      if (item.nonTaxableDate) {
        const formattedDate = getUniversalDateTimeFormat({
          dateTime: item?.nonTaxableDate,
        });
        datesSet.add(formattedDate);
      }
    });

    return {
      categoryType,
      amount: totalAmount || 0,
      notes: Array.from(notesSet).join("\n"),
      dates: Array.from(datesSet).join("\n"),
      nonTaxableCharges: items,
    };
  });

  return rows;
}

export function getNonTaxableCategoryTotals({ nonTaxableCharges = [] }) {
  if (!Array.isArray(nonTaxableCharges))
    return {
      categoryTotal: [],
      grandTotalAmount: 0,
    };

  const filteredCat = nonTaxableCharges?.filter(
    (n) => n?.nonTaxableCategoryType,
  );

  // Group charges by category nonTaxableCategoryType
  const grouped = _.groupBy(filteredCat, "nonTaxableCategoryType");

  // Calculate totals per category
  const categoryTotal = Object.entries(grouped).map(([category, items]) => {
    const total = items.reduce(
      (sum, item) => sum + (item.nonTaxableAmount || 0),
      0,
    );
    return {
      category,
      total,
    };
  });

  // Calculate grand total
  const grandTotalAmount = categoryTotal.reduce((sum, c) => sum + c.total, 0);

  return {
    categoryTotal,
    grandTotalAmount,
  };
}

export function getReimbursementCategoryTotals({ reimbursements = [], groupByContractor = false }) {
  if (!Array.isArray(reimbursements))
    return {
      categoryTotal: [],
      grandTotalAmount: 0,
    };

  const filteredCat = reimbursements?.filter(
    (n) =>
      n?.isDeleted !== true && (n?.reImbursementCategoryType || n?.reImbursementType) &&
      [true, 'true', 1, '1'].includes(n?.reImburseAddInClientInvoice),
  );

  // Group charges by category nonTaxableCategoryType
  const grouped = _.groupBy(
    filteredCat,
    (i) => groupByContractor
      ? JSON.stringify([i?.reImbursementCategoryType || i?.reImbursementType, String(i?.contractorId || i?.contractorName || ''), String(i?.contractorName || '')])
      : i?.reImbursementCategoryType || i?.reImbursementType,
  );

  // Calculate totals per category
  const categoryTotal = Object.entries(grouped).map(([key, items]) => {
    const category = groupByContractor ? JSON.parse(key)[0] : key;
    const contractorName = groupByContractor ? JSON.parse(key)[2] : '';
    const total = items.reduce(
      (sum, item) => sum + (item.approvedAmount || 0),
      0,
    );
    return {
      category,
      total,
      ...(contractorName ? { contractorName } : {}),
    };
  });

  // Calculate grand total
  const grandTotalAmount = categoryTotal.reduce((sum, c) => sum + c.total, 0);

  return {
    categoryTotal,
    grandTotalAmount,
  };
}

export function getDeductionCategoryTotals({ deductions = [] }) {
  if (!Array.isArray(deductions))
    return {
      categoryTotal: [],
      grandTotalAmount: 0,
    };

  const filteredCat = deductions?.filter(
    (n) => n?.deductionCategoryType && n?.deductionFromClientInvoice,
  );

  // Group charges by category nonTaxableCategoryType
  const grouped = _.groupBy(filteredCat, (i) => i?.deductionCategoryType);

  // Calculate totals per category
  const categoryTotal = Object.entries(grouped).map(([category, items]) => {
    const total = items.reduce(
      (sum, item) => sum + (item.deductionAmount || 0),
      0,
    );
    return {
      category,
      total,
    };
  });

  // Calculate grand total
  const grandTotalAmount = categoryTotal.reduce((sum, c) => sum + c.total, 0);

  return {
    categoryTotal,
    grandTotalAmount,
  };
}
